import Promise from 'bluebird';
import React, { useCallback, useMemo } from 'react';

import { ChartDataAdapter } from '@bingads-webui-campaign-react/inline-chart';
import appConfig from '@bingads-webui-component-legacy/config/index';
import { InlineChartPreferences } from '@bingads-webui-campaign/inline-chart-preferences';
import { store, adapter } from '@bingads-webui-component-legacy/bingads-grid/service/grid-preferences';
import preferenceServiceLegacy from '@bingads-webui-component-legacy/preference/service/index';
import globalPageContext from '@bingads-webui-static-legacy/page-context';
import appState from '@bingads-webui-component-legacy/state/index';
import { AdStudioInlineChartWrapper } from '../inline-chart-wrapper';

export const useAdStudioInlineChart = ({
  serverContext, globalLegacyI18n, getPageContext, forceToResizeChart,
}) => {
  const pageContext = useMemo(() => (getPageContext ? getPageContext() : globalPageContext), [getPageContext]);
  const i18nInlineChart = useMemo(() => globalLegacyI18n && globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!@bingads-webui-campaign-react/inline-chart'), [globalLegacyI18n]);

  const chartPreference = useMemo(() => {
    const preferencePromise = preferenceServiceLegacy.initialize().then(() => Promise.promisifyAll(preferenceServiceLegacy));
    return new InlineChartPreferences(
      store,
      appConfig,
      adapter,
      preferencePromise
    );
  }, []);

  const chartDataAdapter = useMemo(() => new ChartDataAdapter({
    i18n: i18nInlineChart,
    permissions: pageContext.Permissions,
    gridName: pageContext.CurrentGridName,
    lowestSupportedGranularity: 'Day',
  }), [i18nInlineChart, pageContext]);

  const getChartPreference = useCallback(() => chartDataAdapter.getPreferencesAndDefaults(
    chartPreference,
    pageContext
  ), [chartDataAdapter, chartPreference, pageContext]);
  pageContext.CurrentTabId = 'Asset';

  const renderInlineChart = ({
    isChartShown, filters, loadPerfTimeSeriesData, perfMetricDateRange, perfMarker,
  }) => (
    <AdStudioInlineChartWrapper
      isShowChart={isChartShown}
      perfMarker={perfMarker}
      serverContext={serverContext}
      loadPerfTimeSeriesData={loadPerfTimeSeriesData}
      dateFilter={perfMetricDateRange}
      chartPreference={chartPreference}
      pageContext={pageContext}
      i18nInlineChart={i18nInlineChart}
      filters={filters}
      appState={appState}
      forceToResizeChart={forceToResizeChart}
    />
  );

  const getGranularityEnum = useCallback(perfMetricDateRange => getChartPreference().then(({ granularityId }) => {
    const dateRange = { ...perfMetricDateRange, start: new Date(perfMetricDateRange.RangeStartDate), end: new Date(perfMetricDateRange.RangeEndDate) };
    if (chartDataAdapter.reevaluateGranularity && chartDataAdapter.getGranularityEnum) {
      const newGranularityId = chartDataAdapter.reevaluateGranularity(dateRange, granularityId);
      return chartDataAdapter.getGranularityEnum(newGranularityId);
    }
    return 'Day';
  }), [chartDataAdapter, getChartPreference]);

  return { renderInlineChart, getChartPreference, getGranularityEnum };
};
